import { type RovoLabsConfirmedTools } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { MessageActionInvocationStatus } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { StoreActionLoggableName } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { createSafeAction, type SafeAction } from './createSafeAction';
import { getAgentMessageById } from './getAgentMessageById';
import type { ChatStoreAction } from './types';

type UpdateConfirmedToolsActionStatusesArgs = {
	conversationId: string;
	messageId: string;
	tools: RovoLabsConfirmedTools['tools'];
	status: MessageActionInvocationStatus | 'fromResolution';
};

export const updateConfirmedToolsActionStatuses: SafeAction<
	[UpdateConfirmedToolsActionStatusesArgs]
> = createSafeAction(
	StoreActionLoggableName.updateActionInvocationStatus,
	({
		conversationId,
		messageId,
		tools,
		status,
	}: UpdateConfirmedToolsActionStatusesArgs): ChatStoreAction =>
		async ({ getState }, { getMessages, setMessages }) => {
			const currentConversation = getState().conversations[conversationId];
			if (!currentConversation) {
				return;
			}

			const messages = getMessages(conversationId);
			const agentMessage = getAgentMessageById(messages, messageId);

			if (!agentMessage.result) {
				return;
			}

			const updatedInvokedActions = { ...agentMessage.result.invokedActions };
			for (const tool of tools) {
				const resolvedStatus: MessageActionInvocationStatus =
					status === 'fromResolution'
						? tool.resolution?.type === 'cancelled'
							? 'cancelled'
							: 'finished'
						: status;
				updatedInvokedActions[tool.invocation_id] = resolvedStatus;
			}

			const updatedMessages = [...messages];
			updatedMessages[agentMessage.index] = {
				...agentMessage.result,
				invokedActions: updatedInvokedActions,
			};

			setMessages(conversationId, updatedMessages);
		},
);
